import { useState, useEffect } from 'react';
import { usePortfolio } from '../context/PortfolioContext';
import { portfolioApi } from '../api/client';
import toast from 'react-hot-toast';

function RiskPage() {
  const { holdings } = usePortfolio();
  const [riskData, setRiskData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRiskData = async () => {
      try {
        const data = await portfolioApi.getRisk();
        setRiskData(data);
      } catch (error) {
        toast.error('Failed to load risk data');
        console.error('Risk fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRiskData();
  }, []);

  // Fallback to local calculation if backend data not available
  const metrics = riskData?.metrics || calculateLocalMetrics(holdings);
  const hasBackendData = !!riskData;

  function calculateLocalMetrics(holdings) {
    if (!holdings?.length) return null;

    const totalValue = holdings.reduce((sum, h) => sum + (h.market_value_gbp || h.mvGbp || 0), 0);
    
    const maxPosition = Math.max(...holdings.map(h => 
      totalValue > 0 ? ((h.market_value_gbp || h.mvGbp || 0) / totalValue) * 100 : 0
    ));

    const numPositions = holdings.filter(h => h.quantity > 0).length;

    const currencyExposure = {};
    holdings.forEach(h => {
      // Use the currency field from holding data (GBP, USD, EUR, CAD, etc.)
      const ccy = h.currency || h.price_currency || 'GBP';
      if (!currencyExposure[ccy]) currencyExposure[ccy] = 0;
      currencyExposure[ccy] += h.market_value_gbp || h.mvGbp || 0;
    });

    const unrealizedPnLs = holdings.map(h => h.unrealized_gbp || 0).filter(v => v !== 0);
    const avgPnL = unrealizedPnLs.reduce((a, b) => a + b, 0) / unrealizedPnLs.length || 0;
    const variance = unrealizedPnLs.reduce((sum, val) => sum + Math.pow(val - avgPnL, 2), 0) / (unrealizedPnLs.length || 1);
    const volatility = Math.sqrt(variance);

    return {
      concentration: {
        max_position_pct: maxPosition.toFixed(1),
        positions_above_15pct: holdings.filter(h => 
          totalValue > 0 && ((h.market_value_gbp || h.mvGbp || 0) / totalValue) * 100 > 15
        ).length,
        risk_level: maxPosition > 25 ? 'high' : maxPosition > 15 ? 'medium' : 'low',
      },
      currency_exposure: {
        currencies: Object.entries(currencyExposure).map(([currency, value]) => ({
          currency,
          value_gbp: value,
          percentage: totalValue > 0 ? (value / totalValue) * 100 : 0,
        })),
      },
      portfolio_summary: {
        open_positions: numPositions,
      },
      volatility: Math.abs(volatility),
      totalValue,
    };
  }

  const formatCurrency = (value) => {
    if (!value || value === '-') return '-';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value) => {
    if (!value || value === '-') return '-';
    return `${Number(value).toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="loading-screen" style={{ minHeight: '400px' }}>
        <div className="loading-spinner"></div>
        <p>Loading risk analysis...</p>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">R</div>
        <p>No holdings data available for risk analysis</p>
      </div>
    );
  }

  // Use backend data when available, fallback to local
  const concentration = metrics.concentration || {};
  const portfolioSummary = metrics.portfolio_summary || {};
  const totalValue = metrics.totalValue || 0;
  
  // Always use local calculation for currency exposure to ensure accuracy
  // (backend may return outdated or incomplete currency data)
  const localMetrics = calculateLocalMetrics(holdings) || {};
  const currencyExposure = localMetrics.currency_exposure || { currencies: [] };

  return (
    <div>
      {/* Risk Metrics Grid */}
      <div className="dashboard-grid dashboard-grid-4" style={{ marginBottom: '1.5rem' }}>
        <div className="summary-card orange">
          <div className="summary-card-label">Max Position</div>
          <div className="summary-card-value">{formatPercent(concentration.max_position_pct || metrics?.concentration?.max_position_pct)}</div>
          <div className="summary-card-sub">{concentration.positions_above_15pct || 0} above 15%</div>
        </div>
        <div className="summary-card blue">
          <div className="summary-card-label">Positions</div>
          <div className="summary-card-value">{portfolioSummary.open_positions || holdings?.length || 0}</div>
          <div className="summary-card-sub">Number of holdings</div>
        </div>
        <div className="summary-card purple">
          <div className="summary-card-label">Risk Level</div>
          <div className="summary-card-value" style={{ 
            color: concentration.risk_level === 'high' ? 'var(--red)' : 
                   concentration.risk_level === 'medium' ? 'var(--orange)' : 'var(--green)',
            textTransform: 'uppercase'
          }}>
            {concentration.risk_level || 'low'}
          </div>
          <div className="summary-card-sub">{hasBackendData ? 'From backend' : 'Local calc'}</div>
        </div>
        <div className="summary-card cyan">
          <div className="summary-card-label">Total Exposure</div>
          <div className="summary-card-value">{formatCurrency(totalValue)}</div>
          <div className="summary-card-sub">Market value at risk</div>
        </div>
      </div>

      {/* Currency Exposure */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-header">
          <span className="card-title">Currency Exposure</span>
          <span className="data-table-badge">FX Risk</span>
        </div>
        <div style={{ padding: '1rem' }}>
          <div className="dashboard-grid dashboard-grid-4">
            {(currencyExposure.currencies || [])
              .sort((a, b) => b.value_gbp - a.value_gbp)
              .map((item) => {
                return (
                  <div key={item.currency} className="card" style={{ background: 'var(--bg-light)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '1.25rem', fontWeight: 700 }}>{item.currency}</span>
                      <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>{item.percentage?.toFixed(1)}%</span>
                    </div>
                    <div style={{ fontSize: '1rem', fontFamily: 'Space Mono' }}>
                      {formatCurrency(item.value_gbp)}
                    </div>
                    <div style={{ 
                      height: '4px', 
                      background: 'var(--bg)', 
                      borderRadius: '2px',
                      marginTop: '0.75rem',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${item.percentage}%`,
                        height: '100%',
                        background: item.currency === 'GBP' ? 'var(--green)' : 
                                   item.currency === 'USD' ? 'var(--cyan)' : 
                                   item.currency === 'EUR' ? 'var(--orange)' : 
                                   item.currency === 'CAD' ? 'var(--pink)' : 'var(--purple)',
                        borderRadius: '2px',
                      }} />
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {/* Risk Warnings */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Risk Assessment</span>
        </div>
        <div style={{ padding: '1rem' }}>
          {concentration.risk_level === 'high' && (
            <div style={{ 
              padding: '1rem', 
              background: 'rgba(255, 61, 90, 0.1)', 
              border: '1px solid var(--red)',
              borderRadius: '0.5rem',
              marginBottom: '1rem'
            }}>
              <strong style={{ color: 'var(--red)', display: 'block', marginBottom: '0.5rem' }}>
                ⚠️ High Concentration Risk
              </strong>
              <p style={{ margin: 0, fontSize: '0.875rem' }}>
                Your largest position represents {concentration.max_position_pct}% of your portfolio. 
                This high concentration increases your vulnerability to single-stock risk.
              </p>
            </div>
          )}
          
          {concentration.risk_level === 'medium' && (
            <div style={{ 
              padding: '1rem', 
              background: 'rgba(251, 146, 60, 0.1)', 
              border: '1px solid var(--orange)',
              borderRadius: '0.5rem',
              marginBottom: '1rem'
            }}>
              <strong style={{ color: 'var(--orange)', display: 'block', marginBottom: '0.5rem' }}>
                ⚠️ Moderate Concentration Risk
              </strong>
              <p style={{ margin: 0, fontSize: '0.875rem' }}>
                Your largest position is {concentration.max_position_pct}% of your portfolio.
                Consider rebalancing to reduce concentration.
              </p>
            </div>
          )}
          
          {concentration.positions_above_15pct > 0 && (
            <div style={{ 
              padding: '1rem', 
              background: 'rgba(251, 146, 60, 0.1)', 
              border: '1px solid var(--orange)',
              borderRadius: '0.5rem',
              marginBottom: '1rem'
            }}>
              <strong style={{ color: 'var(--orange)', display: 'block', marginBottom: '0.5rem' }}>
                ⚠️ Multiple Concentrated Positions
              </strong>
              <p style={{ margin: 0, fontSize: '0.875rem' }}>
                You have {concentration.positions_above_15pct} position(s) exceeding 15% of your portfolio.
              </p>
            </div>
          )}

          {concentration.risk_level === 'low' && concentration.positions_above_15pct === 0 && (
            <div style={{ 
              padding: '1rem', 
              background: 'rgba(0, 229, 160, 0.1)', 
              border: '1px solid var(--green)',
              borderRadius: '0.5rem'
            }}>
              <strong style={{ color: 'var(--green)', display: 'block', marginBottom: '0.5rem' }}>
                ✓ Well Diversified
              </strong>
              <p style={{ margin: 0, fontSize: '0.875rem' }}>
                Your portfolio is well diversified with no single position exceeding 15%.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RiskPage;
